import { esc } from "../ui/format.js";
import { uiGet, uiMoney, uiPays } from "../ui/kit.js";
import { emAct, emLetters, emLog, emMeter, emRegister, emWireLetters } from "./empire.js";
import { nmFace } from "./nemesis.js";

/**
 * Your rival, as one person (backend rival_ceo.py on nemesis.py; 2026-10-22).
 *
 *   Competitors   "Your rival": the CEO's face, the feud, what they did to you
 *                 (letters: their launch price cut, a poach, a copycat, the
 *                 Nemesis moves), their headlines, and your strikes back.
 *   M&A           what your rival bought after you passed on it (rival_deals.py).
 *   Investments   the same list, for the startups you lost in the Tank.
 */

const RV_MEMORY_TONE = { them: "neg", you: "pos", win: "pos", start: "" };

function rvStrike(m) {
  const why = !m.ready ? "One strike a month" : m.affordable ? "" : "Not enough cash";
  return `<button type="button" class="rv-strike" data-rv-strike="${esc(m.key)}" ${why ? "disabled" : ""} title="${esc(m.detail)}">
    <span class="rv-strike-i">${m.icon}</span><b>${esc(m.label)}</b><span>${esc(m.detail)}</span>
    <em>${uiMoney(m.cost)}${m.odds ? ` · ${m.odds}% odds` : ""}${why ? ` · ${esc(why)}` : ""}</em>${uiPays(m)}</button>`;
}

async function rivalPanel(host) {
  const v = await uiGet("/rival");
  if (!v.ceo) {
    host.innerHTML = `<p class="sx-card-sub">${esc(v.note || "No rival CEO yet. Around month 2 the most aggressive rival decides you're the one to beat.")}</p>`;
    return { title: "Your rival", summary: "not yet", open: false };
  }
  const move = v.open_move;
  const letters = v.letters || [];
  const rec = v.record || { won: 0, lost: 0, truces: 0 };
  host.innerHTML = `
    <section class="rv-hero">
      <div class="rv-face">${nmFace(v.ceo.face_seed, 120)}</div>
      <div class="rv-id">
        <span class="nm-kicker">${v.status === "beaten" ? "Your former rival · beaten" : `Your rival since month ${v.ceo.since_month}`}</span>
        <h3>${esc(v.ceo.name)}</h3>
        <div class="nm-sub">${esc(v.ceo.title)}, <b>${esc(v.rival.name || "")}</b> · ${esc(v.ceo.personality || "")}</div>
        <div class="nm-record"><span><b>${rec.won}</b> won</span><span><b>${rec.lost}</b> lost</span><span><b>${rec.truces}</b> truces</span><span class="nm-stage">${esc(v.stage_label || "")}</span></div>
        <div class="rv-h2h">
          <span>Their share <b>${(v.rival.market_share_pct || 0).toFixed(1)}%</b></span>
          <span>Their revenue <b>${uiMoney(v.rival.monthly_revenue || 0, { compact: true })}/mo</b></span>
          <span>Reputation <b>${Math.round(v.rival.reputation || 0)}</b></span>
          <span>Aggression <b>${Math.round(v.rival.aggression || 0)}</b></span>
        </div>
      </div>
    </section>
    ${v.headlines && v.headlines.length ? `<div class="rv-ticker" aria-label="Their latest headlines">${v.headlines.slice(0, 3).map((h) => `<p><b>M${h.month}</b> ${esc(h.text)}</p>`).join("")}</div>` : ""}
    ${emMeter(v.feud || 0, { label: "Feud", lines: (v.stages || []).slice(1).map((s) => ({ at: s.at, label: s.label })) })}
    ${move ? `<article class="nm-letter"><div class="nm-letter-kick">${esc(v.stage_label)} · answer this month</div><h3>${esc(move.title)}</h3>
      <blockquote>“${esc(move.quote)}”<cite>${esc(v.ceo.name)}</cite></blockquote><p>${esc(move.body)}</p>
      <div class="nm-options">${move.options.map((o) => `<button type="button" class="nm-opt" data-rv-nm="${esc(o.key)}" ${o.affordable === false ? "disabled" : ""}>
        <b>${esc(o.label)}</b><span>${esc(o.detail)}</span><em>${o.cost ? uiMoney(o.cost) : "Free"}${o.odds ? ` · ${o.odds}% odds` : ""}</em>${uiPays(o)}</button>`).join("")}</div></article>` : ""}
    ${emLetters(letters)}
    <h4 class="rv-h">Strike back <span class="sx-card-sub">${esc(v.strike_note || "")}</span></h4>
    <div class="rv-strikes">${(v.strikes || []).map(rvStrike).join("")}</div>
    ${v.can_buy ? `<p class="sx-card-sub">They're small enough to buy outright: <button type="button" class="mv-chip" data-rv-buy>See it in M&A</button></p>` : ""}
    <div class="mv-grid">
      <section class="mv-card"><h4>What you did to them</h4>${emLog(v.strikes_log.map((s) => ({ month: s.month, text: `${s.label}: ${s.result}` })), { emptyText: "Nothing yet." })}</section>
      <section class="mv-card"><h4>What they remember</h4><ol class="nm-memory">${(v.memory || []).slice(0, 6).map((m) => `<li class="nm-m-${esc(m.kind)} ${RV_MEMORY_TONE[m.kind] || ""}"><b>M${m.month}</b><span>${esc(m.text)}</span></li>`).join("")}</ol></section>
    </div>`;
  emWireLetters(host);
  host.querySelectorAll("[data-rv-strike]").forEach((b) => b.addEventListener("click", () => emAct("rival_strike", { move: b.dataset.rvStrike })));
  host.querySelectorAll("[data-rv-nm]").forEach((b) => b.addEventListener("click", () => emAct("nemesis_respond", { option: b.dataset.rvNm })));
  const buy = host.querySelector("[data-rv-buy]");
  if (buy) buy.addEventListener("click", () => window.ges && window.ges.showTab("ma"));
  const waiting = (move ? 1 : 0) + letters.length;
  return { title: `Your rival: ${v.ceo.name}, ${v.rival.name || ""}`, summary: `feud ${Math.round(v.feud || 0)}/100${waiting ? " · waiting for your answer" : ""}`, open: true, badge: waiting ? "!" : "" };
}

async function catchesPanel(host, kind) {
  const v = await uiGet("/rival-deals");
  const list = (v.catches || []).filter((c) => !kind || c.kind === kind);
  if (!v.watching && !list.length) return null;
  host.innerHTML = `${v.watching ? `<p class="rv-watch">👁 ${esc(v.watching.note)}</p>` : ""}
    ${emLog(list.map((c) => ({ month: c.month, text: c.note })), { emptyText: "Nothing taken from you yet." })}`;
  return { title: "What your rival takes when you pass", summary: list.length ? `${list.length} so far` : "watching", open: false };
}

emRegister("competitors", rivalPanel);
emRegister("ma", (h) => catchesPanel(h, "ma"));
emRegister("investments", (h) => catchesPanel(h, "tank"));

export { rivalPanel };
